/**
 * PasswordSafeDashboard — full-page power dashboard (own page, like the
 * Bookkeeper) for the encrypted per-company credential repository.
 *
 * Ground rules mirrored from the backend:
 *  - The list never contains passwords; reveal/copy calls POST /:id/reveal
 *    and the plaintext lives only in transient state until hidden.
 *  - A user only sees entries they own or were granted; the grants editor
 *    (owner only — the original creator or the principal-owner user, as
 *    reported by the server's `can_manage`) controls who else can see the
 *    entry exists. created_by is NOT the owner: SITAC re-stamps it to the
 *    last editor, which is why the server exposes the stable creator_id.
 *  - Principal owner is a USER or a CONTACT (searched, must exist).
 *  - `quality` is a 0-100 strength score computed here and stored so lists
 *    can show the meter without shipping the password.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, HStack, VStack, Input, Select, FormControl, FormLabel, Textarea,
  Box, Text, Badge, Flex, IconButton, useToast, Progress, Tag, TagLabel,
  TagCloseButton, Wrap, InputGroup, InputRightElement, Tooltip, Spinner,
} from '@chakra-ui/react';
import {
  FiKey, FiPlus, FiEye, FiEyeOff, FiCopy, FiEdit2, FiTrash2, FiExternalLink,
  FiRefreshCw, FiLock, FiRotateCcw,
} from 'react-icons/fi';
import api from '../api';
import PageShell from './PageShell';
import ConfirmDialog from './ConfirmDialog';
import UserTransferList from './UserTransferList';
import { useAuth } from '../contexts/AuthContext';

// ── Password strength (zxcvbn-lite): length + character-class variety with
// penalties for repeats and sequences. 0-100.
export function passwordQuality(pw) {
  if (!pw) return 0;
  let score = Math.min(40, pw.length * 3);
  if (/[a-z]/.test(pw)) score += 10;
  if (/[A-Z]/.test(pw)) score += 12;
  if (/[0-9]/.test(pw)) score += 12;
  if (/[^A-Za-z0-9]/.test(pw)) score += 16;
  if (/(.)\1{2,}/.test(pw)) score -= 12;                       // aaa
  if (/(?:0123|1234|2345|3456|4567|5678|6789|abcd|qwer|asdf)/i.test(pw)) score -= 15;
  if (/^(password|letmein|welcome|admin|monkey)/i.test(pw)) score -= 30;
  return Math.max(0, Math.min(100, score));
}
const qualityColor = (q) => q >= 75 ? 'green' : q >= 50 ? 'yellow' : q >= 25 ? 'orange' : 'red';
const qualityLabel = (q) => q >= 75 ? 'Strong' : q >= 50 ? 'Fair' : q >= 25 ? 'Weak' : 'Very weak';

export function genPassword(len = 18) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_=+';
  const buf = new Uint32Array(len);
  window.crypto.getRandomValues(buf);
  return [...buf].map(n => chars[n % chars.length]).join('');
}

const emptyForm = () => ({
  name: '', username: '', password: '', url: '', notes: '',
  company_id: '', owner_type: 'user', owner_id: '', owner_name: '',
  user_ids: [],
  entry_type: 'account',   // 'account' (own credentials) | 'sso' (signs in via another entry)
  sso_entry_id: '',
});

export default function PasswordSafeDashboard({ fullPage, onBack, isOpen, onClose }) {
  const active = fullPage || isOpen;
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [versionView, setVersionView] = useState('current'); // current | previous | deleted
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null);          // null = list; object = editor
  const [editingId, setEditingId] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({});    // entryId -> plaintext (transient)
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerResults, setOwnerResults] = useState([]);
  const toast = useToast();

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('view', versionView);
      if (companyFilter) params.set('company_id', companyFilter);
      const { data } = await api.get(`/password-safe?${params}`);
      setEntries(Array.isArray(data) ? data : []);
    } catch (e) { setEntries([]); }
    finally { setLoading(false); }
  }, [companyFilter, versionView]);

  useEffect(() => {
    if (!active) return;
    fetchEntries();
    setRevealed({});
    api.get('/companies?per_page=1000').then(r => {
      const d = r.data?.data || r.data;
      setCompanies(Array.isArray(d) ? d : []);
    }).catch(() => {});
    api.get('/calendar/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [active, fetchEntries]);

  // ── Owner (user OR contact) type-ahead ──
  useEffect(() => {
    if (!form || !ownerSearch.trim()) { setOwnerResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        if (form.owner_type === 'contact') {
          const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(ownerSearch)}&limit=8`);
          if (!cancelled) setOwnerResults((Array.isArray(data) ? data : []).map(c => ({
            id: c.id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email })));
        } else {
          const q = ownerSearch.toLowerCase();
          if (!cancelled) setOwnerResults(users
            .filter(u => (u.display_name || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q))
            .slice(0, 8).map(u => ({ id: u.id, name: u.display_name || u.username })));
        }
      } catch (e) { if (!cancelled) setOwnerResults([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ownerSearch, form?.owner_type, users, form]);

  const openCreate = () => {
    // Principal owner defaults to the logged-in user — removable via the
    // chip's X, then search to pick another user or contact.
    setForm({ ...emptyForm(),
      owner_type: 'user',
      owner_id: user?.id || '',
      owner_name: user?.display_name || user?.username || '',
    });
    setEditingId(null); setShowPw(false); setOwnerSearch('');
  };
  const openEdit = async (entry) => {
    try {
      const { data } = await api.get(`/password-safe/${entry.id}`);
      setForm({
        name: data.name || '', username: data.username || '', password: '',
        url: data.url || '', notes: data.notes || '',
        entry_type: data.entry_type === 'sso' ? 'sso' : 'account',
        sso_entry_id: data.sso_entry_id || '',
        company_id: data.company_id || '',
        owner_type: data.owner_contact_id ? 'contact' : 'user',
        owner_id: data.owner_contact_id || data.owner_user_id || '',
        owner_name: data.owner_contact_name || data.owner_user_name || '',
        // The owner is pinned in the transfer list, so keep them out of the
        // removable list (their grant row is always preserved server-side).
        user_ids: (data.granted_users || [])
          .filter(u => String(u.id) !== String(data.creator_id || ''))
          .map(u => ({ id: u.id, name: u.display_name || u.username })),
        can_manage: data.can_manage ?? data.is_creator,
        pin_id: data.creator_id || user?.id || '',
        pin_name: data.creator_name || user?.display_name || user?.username || '',
      });
      setEditingId(entry.id); setShowPw(false); setOwnerSearch('');
    } catch (e) { toast({ title: 'Could not open entry', status: 'error', duration: 2000 }); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return; }
    if (!editingId && form.entry_type === 'account' && !form.password) { toast({ title: 'Password required', status: 'warning', duration: 2000 }); return; }
    if (form.entry_type === 'sso' && !form.sso_entry_id) { toast({ title: 'Choose the linked Single Sign-On account', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, username: form.username, url: form.url, notes: form.notes,
        company_id: form.company_id || null,
        owner_type: form.owner_type, owner_id: form.owner_id || null,
        user_ids: (editingId && form.can_manage === false) ? undefined : form.user_ids.map(u => u.id),
        entry_type: form.entry_type,
        sso_entry_id: form.entry_type === 'sso' ? form.sso_entry_id : null,
        quality: form.entry_type === 'account' && form.password ? passwordQuality(form.password) : undefined,
      };
      if (form.entry_type === 'account' && form.password) payload.password = form.password;
      if (editingId) await api.put(`/password-safe/${editingId}`, payload);
      else await api.post('/password-safe', payload);
      toast({ title: editingId ? 'Entry updated' : 'Entry created', status: 'success', duration: 1500 });
      setForm(null); setEditingId(null);
      await fetchEntries();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.error || '', status: 'error', duration: 2500 });
    } finally { setSaving(false); }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const handleDelete = (entry) => setDeleteTarget(entry);
  const confirmDelete = async () => {
    const entry = deleteTarget; if (!entry) return;
    try { await api.delete(`/password-safe/${entry.id}`); toast({ title: 'Deleted (retained for audit)', status: 'info', duration: 1500 }); fetchEntries(); }
    catch (e) { toast({ title: e?.response?.data?.error || 'Failed', status: 'error', duration: 2500 }); }
    setDeleteTarget(null);
  };

  const reveal = async (entry) => {
    if (revealed[entry.id] !== undefined) { // toggle off — drop the plaintext
      setRevealed(p => { const n = { ...p }; delete n[entry.id]; return n; });
      return;
    }
    try {
      const { data } = await api.post(`/password-safe/${entry.id}/reveal`);
      setRevealed(p => ({ ...p, [entry.id]: data.password || '' }));
    } catch (e) { toast({ title: 'Could not reveal', description: e?.response?.data?.error || '', status: 'error', duration: 2500 }); }
  };

  const copyPassword = async (entry) => {
    try {
      let pw = revealed[entry.id];
      if (pw === undefined) {
        const { data } = await api.post(`/password-safe/${entry.id}/reveal`);
        pw = data.password || '';
      }
      await navigator.clipboard.writeText(pw);
      toast({ title: 'Password copied', status: 'success', duration: 1200 });
    } catch (e) { toast({ title: 'Copy failed', status: 'error', duration: 2000 }); }
  };

  // ── Editor view ──
  if (form) {
    const q = passwordQuality(form.password);
    return (
      <PageShell fullPage={fullPage} onBack={() => setForm(null)} isOpen={true} onClose={() => setForm(null)}
        title={editingId ? 'Edit Password Entry' : 'New Password Entry'}>
        <Box maxW="760px" mx="auto" w="100%">
          <VStack spacing={3} align="stretch">
            <HStack spacing={3}>
              <FormControl flex={2} isRequired><FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
                <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Sage portal login" /></FormControl>
              <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Company (client or supplier)</FormLabel>
                <Select size="sm" value={form.company_id} onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))}>
                  <option value="">—</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select></FormControl>
            </HStack>
            <HStack spacing={3}>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Username</FormLabel>
                <Input size="sm" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} /></FormControl>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>URL</FormLabel>
                <Input size="sm" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://…" /></FormControl>
            </HStack>
            {/* Credential type: an Account holds its own password; Single
                Sign-On links to another entry that provides the login —
                e.g. "Claude" signs in with the "Google" account. */}
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>Credential Type</FormLabel>
              <HStack>
                <Select size="sm" w="200px" value={form.entry_type}
                  onChange={e => setForm(p => ({ ...p, entry_type: e.target.value }))}>
                  <option value="account">Account (username &amp; password)</option>
                  <option value="sso">Single Sign-On (via another account)</option>
                </Select>
                {form.entry_type === 'sso' && (
                  <Select size="sm" flex={1} value={form.sso_entry_id}
                    onChange={e => setForm(p => ({ ...p, sso_entry_id: e.target.value }))}>
                    <option value="">Signs in with…</option>
                    {entries.filter(e2 => String(e2.id) !== String(editingId)).map(e2 => (
                      <option key={e2.id} value={e2.id}>{e2.name}{e2.company_name ? ` — ${e2.company_name}` : ''}</option>
                    ))}
                  </Select>
                )}
              </HStack>
              {form.entry_type === 'sso' && (
                <Text fontSize="9px" color="gray.400" mt={1}>
                  No password is stored on this entry — reveal and copy use the linked account's password
                  (you need access to that entry too).
                </Text>
              )}
            </FormControl>
            {form.entry_type === 'account' && (
            <FormControl isRequired={!editingId}>
              <FormLabel fontSize="xs" mb={0.5}>Password {editingId ? '(leave blank to keep current)' : ''}</FormLabel>
              <HStack>
                <InputGroup size="sm">
                  <Input type={showPw ? 'text' : 'password'} value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))} autoComplete="new-password" />
                  <InputRightElement>
                    <IconButton size="xs" variant="ghost" aria-label="show"
                      icon={showPw ? <FiEyeOff /> : <FiEye />} onClick={() => setShowPw(s => !s)} />
                  </InputRightElement>
                </InputGroup>
                <Button size="sm" variant="outline" leftIcon={<FiRefreshCw size={12} />}
                  onClick={() => { setForm(p => ({ ...p, password: genPassword() })); setShowPw(true); }}>
                  Generate
                </Button>
              </HStack>
              {form.password && (
                <HStack mt={1} spacing={2}>
                  <Progress value={q} size="xs" colorScheme={qualityColor(q)} flex={1} borderRadius="full" />
                  <Text fontSize="10px" color={`${qualityColor(q)}.600`} fontWeight="700" w="70px">{qualityLabel(q)} ({q})</Text>
                </HStack>
              )}
            </FormControl>
            )}

            {/* Principal owner: user OR contact */}
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>Principal Owner</FormLabel>
              <HStack align="start">
                <Select size="sm" w="120px" value={form.owner_type}
                  onChange={e => setForm(p => ({ ...p, owner_type: e.target.value, owner_id: '', owner_name: '' }))}>
                  <option value="user">User</option>
                  <option value="contact">Contact</option>
                </Select>
                {form.owner_id ? (
                  <Tag size="md" colorScheme="purple">
                    <TagLabel>{form.owner_name}</TagLabel>
                    <TagCloseButton onClick={() => setForm(p => ({ ...p, owner_id: '', owner_name: '' }))} />
                  </Tag>
                ) : (
                  <Box flex={1} position="relative">
                    <Input size="sm" placeholder={`Search ${form.owner_type}s…`} value={ownerSearch}
                      onChange={e => setOwnerSearch(e.target.value)} />
                    {ownerResults.length > 0 && (
                      <Box position="absolute" top="100%" left={0} right={0} zIndex={20} bg="white"
                        border="1px" borderColor="gray.200" borderRadius="md" boxShadow="md" maxH="160px" overflowY="auto">
                        {ownerResults.map(r => (
                          <Box key={r.id} px={2} py={1} fontSize="12px" cursor="pointer" _hover={{ bg: 'purple.50' }}
                            onClick={() => { setForm(p => ({ ...p, owner_id: r.id, owner_name: r.name })); setOwnerSearch(''); setOwnerResults([]); }}>
                            {r.name}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </HStack>
            </FormControl>

            {/* Visibility — dual-list picker: available users on the left,
                users with access on the right. Creator-only when editing. */}
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>
                Who can see this entry {editingId && form.can_manage === false ? '— only the owner can change this' : ''}
              </FormLabel>
              <UserTransferList users={users} value={form.user_ids}
                onChange={(v) => setForm(p => ({ ...p, user_ids: v }))}
                selfId={editingId ? form.pin_id : user?.id}
                selfName={editingId ? (form.pin_name || 'Owner') : (user?.display_name || user?.username || 'You')}
                disabled={!!editingId && form.can_manage === false} />
              <Text fontSize="9px" color="gray.400" mt={1}>
                Users not on the right cannot see that this entry exists.
              </Text>
            </FormControl>

            <FormControl><FormLabel fontSize="xs" mb={0.5}>Notes</FormLabel>
              <Textarea size="sm" rows={3} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></FormControl>

            <HStack justify="flex-end">
              <Button size="sm" variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
              <Button size="sm" colorScheme="teal" onClick={handleSave} isLoading={saving}>
                {editingId ? 'Save Changes' : 'Create Entry'}
              </Button>
            </HStack>
          </VStack>
        </Box>
      </PageShell>
    );
  }

  // ── List view ──
  const q = search.trim().toLowerCase();
  const visible = entries.filter(e => !q
    || (e.name || '').toLowerCase().includes(q)
    || (e.username || '').toLowerCase().includes(q)
    || (e.company_name || '').toLowerCase().includes(q));
  const groups = {};
  visible.forEach(e => { const k = e.company_name || '(no company)'; (groups[k] = groups[k] || []).push(e); });

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="🔐 Password Safe">
      <Flex mb={3} gap={2} flexWrap="wrap" align="flex-end">
        <FormControl w="220px"><FormLabel fontSize="xs" mb={0.5}>View</FormLabel>
          <Select size="sm" value={versionView} onChange={e => setVersionView(e.target.value)}>
            <option value="current">Show current entries</option>
            <option value="previous">Show Previous Versions</option>
            <option value="deleted">Show Deleted Entries</option>
          </Select></FormControl>
        <FormControl w="280px"><FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
          <Select size="sm" value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
            <option value="">All companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select></FormControl>
        <FormControl w="240px"><FormLabel fontSize="xs" mb={0.5}>Search</FormLabel>
          <Input size="sm" value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, username, company…" /></FormControl>
        <Box flex={1} />
        <Button size="sm" colorScheme="teal" leftIcon={<FiPlus />} onClick={openCreate}>New Entry</Button>
      </Flex>
      <Text fontSize="10px" color="gray.500" mb={3}>
        <FiLock style={{ display: 'inline', verticalAlign: '-2px' }} /> Passwords are stored encrypted (AES-256-GCM) and only
        revealed on demand. You only see entries you own or were granted access to.
      </Text>

      {loading && entries.length === 0 ? (
        <Flex justify="center" py={10}><Spinner /></Flex>
      ) : visible.length === 0 ? (
        <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No entries visible to you{q ? ' match this search' : ' yet'}.</Text>
      ) : (
        <VStack spacing={3} align="stretch">
          {Object.entries(groups).map(([company, list]) => (
            <Box key={company} border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
              <Flex px={3} py={2} bg="purple.50" align="center" gap={2}>
                <FiKey size={13} />
                <Text fontSize="13px" fontWeight="700" flex={1}>{company}</Text>
                <Badge colorScheme="purple" fontSize="9px">{list.length}</Badge>
              </Flex>
              {list.map(e => (
                <Flex key={e.id} px={3} py={2} borderTop="1px" borderColor="gray.100" align="center" gap={3} flexWrap="wrap">
                  <Box minW="180px">
                    <HStack spacing={1} flexWrap="wrap">
                      <Text fontSize="13px" fontWeight="600">{e.name}</Text>
                      {e.version_name && <Badge colorScheme={e.is_version ? 'gray' : 'blue'} fontSize="7px">{e.version_name}</Badge>}
                      {versionView === 'deleted' && (
                        <Badge colorScheme="red" fontSize="7px">DELETED{e.deleted_by_name ? ` by ${e.deleted_by_name}` : ''}</Badge>
                      )}
                      {versionView === 'previous' && e.superseded_to && (
                        <Badge colorScheme="orange" fontSize="7px">Superseded to {e.superseded_to.version_name}</Badge>
                      )}
                    </HStack>
                    <Text fontSize="10px" color="gray.500">{e.username || '—'}{e.created_by_name ? ` · by ${e.created_by_name}` : ''}</Text>
                  </Box>
                  <Box minW="140px">
                    <Text fontSize="9px" color="gray.400">Owner</Text>
                    <Text fontSize="11px">{e.owner_user_name || e.owner_contact_name || '—'}</Text>
                    {e.project_name
                      ? <Badge colorScheme="blue" fontSize="7px" variant="subtle">{e.project_name}</Badge>
                      : (e.company_name ? <Badge colorScheme="purple" fontSize="7px" variant="subtle">All projects</Badge> : null)}
                  </Box>
                  {e.entry_type === 'sso' ? (
                    <Box w="110px">
                      <Badge colorScheme="cyan" fontSize="8px">SSO → {e.sso_entry_name || 'linked account'}</Badge>
                    </Box>
                  ) : e.quality != null && (
                    <Box w="110px">
                      <Progress value={e.quality} size="xs" colorScheme={qualityColor(e.quality)} borderRadius="full" />
                      <Text fontSize="8px" color="gray.400">{qualityLabel(e.quality)}</Text>
                    </Box>
                  )}
                  <Box flex={1} minW="160px">
                    <Tooltip label="Click to copy" fontSize="xs">
                      {revealed[e.id] !== undefined ? (
                        <Text fontSize="12px" fontFamily="mono" bg="yellow.50" px={2} py={0.5} borderRadius="md"
                          display="inline-block" cursor="pointer" onClick={() => copyPassword(e)}>
                          {revealed[e.id] || '(empty)'}
                        </Text>
                      ) : (
                        <Text fontSize="12px" color="gray.400" letterSpacing="2px" display="inline-block"
                          cursor="pointer" onClick={() => copyPassword(e)}>••••••••••</Text>
                      )}
                    </Tooltip>
                  </Box>
                  <HStack spacing={1}>
                    {e.url && (
                      <Tooltip label="Open URL"><IconButton size="xs" variant="ghost" icon={<FiExternalLink size={12} />}
                        aria-label="open" as="a" href={e.url} target="_blank" rel="noopener noreferrer" /></Tooltip>
                    )}
                    <Tooltip label={revealed[e.id] !== undefined ? 'Hide' : 'Reveal'}>
                      <IconButton size="xs" variant="ghost" icon={revealed[e.id] !== undefined ? <FiEyeOff size={12} /> : <FiEye size={12} />}
                        aria-label="reveal" onClick={() => reveal(e)} /></Tooltip>
                    <Tooltip label="Copy password"><IconButton size="xs" variant="ghost" icon={<FiCopy size={12} />}
                      aria-label="copy" onClick={() => copyPassword(e)} /></Tooltip>
                    {versionView === 'current' && (<>
                    <Tooltip label="Edit"><IconButton size="xs" variant="ghost" colorScheme="teal" icon={<FiEdit2 size={12} />}
                      aria-label="edit" onClick={() => openEdit(e)} /></Tooltip>
                    {!!e.can_manage && (
                    <Tooltip label="Delete (owner only; retained for audit)"><IconButton size="xs" variant="ghost" colorScheme="red" icon={<FiTrash2 size={12} />}
                      aria-label="delete" onClick={() => handleDelete(e)} /></Tooltip>
                    )}
                    </>)}
                    {versionView === 'deleted' && !!e.can_manage && (
                      <Tooltip label="Un-delete (owner only; SITAC restore)">
                        <IconButton size="xs" variant="ghost" colorScheme="green" icon={<FiRotateCcw size={12} />} aria-label="restore"
                          onClick={async () => {
                            try { await api.post(`/password-safe/${e.id}/restore`); fetchEntries(); toast({ title: 'Restored', status: 'success', duration: 1500 }); }
                            catch (err) { toast({ title: err?.response?.data?.error || 'Failed', status: 'error', duration: 2500 }); }
                          }} />
                      </Tooltip>
                    )}
                  </HStack>
                </Flex>
              ))}
            </Box>
          ))}
        </VStack>
      )}
      <ConfirmDialog isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Delete Password Entry"
        message={`Are you sure you want to delete \u201C${deleteTarget?.name}\u201D? The entry is retained encrypted for audit and remains viewable under \u201CShow Deleted Entries\u201D (owner only).`}
        confirmLabel="DELETE" onConfirm={confirmDelete} />
    </PageShell>
  );
}
